/* eslint-disable react/button-has-type */
import React from 'react';
import classNames from 'classnames';
import { Tooltip as MuiTooltip } from '@mui/material';
import Icon, { IconName } from '../Icon/Icon';
import styles from './Button.module.scss';

export enum ButtonToneProps {
  PRIMARY_BLUE = 'primary-blue',
  SECONDARY = 'secondary',
  DESTRUCTIVE = 'destructive',
  SUCCESS = 'success',
  DESTRUCTIVE_TRANSPARENT = 'destructive-transparent',
  ERROR = 'error',
  GHOST = 'ghost',
  GHOST_TRANSPARENT = 'ghost-transparent',
}

export enum ButtonPositionProps {
  BEFORE = 'before',
  AFTER = 'after',
}

export interface ExtendedButtonProps extends ButtonProps {
  trailingIcon?: IconName;
}

export interface ButtonProps {
  tone: ButtonToneProps;
  icon?: IconName;
  iconPosition?: ButtonPositionProps;
  customIcon?: React.ReactNode;
  title?: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  maxHeight?: boolean;
  numberIndicator?: number;
  fullOpacity?: boolean;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  hoverText?: string | null;
}

function Button({
  tone = ButtonToneProps.PRIMARY_BLUE,
  icon,
  iconPosition = ButtonPositionProps.BEFORE,
  customIcon,
  title,
  onClick,
  disabled = false,
  className = '',
  maxHeight = false,
  fullOpacity,
  numberIndicator,
  ariaLabel,
  type,
  trailingIcon,
  hoverText = null,
}: ExtendedButtonProps) {
  const buttonElement = (
    <button
      disabled={disabled}
      onClick={onClick}
      type={type || 'button'}
      aria-label={ariaLabel}
      className={classNames(
        styles.root,
        styles[tone],
        className,
        { [styles.maxHeight]: maxHeight },
        { [styles.iconFullOpacity]: fullOpacity },
      )}>
      {iconPosition === ButtonPositionProps.BEFORE && (
        <>
          {icon && !customIcon && <Icon className={title && 'mr-8'} name={icon} />}
          {customIcon && <div className={title && 'mr-8'}>{customIcon}</div>}
        </>
      )}
      {title && <span className="whitespace-nowrap truncate min-w-0">{title}</span>}
      {iconPosition === ButtonPositionProps.AFTER && (
        <>
          {icon && !customIcon && <Icon className={title && 'ml-8'} name={icon} />}
          {customIcon && <div className={title && 'ml-8'}>{customIcon}</div>}
        </>
      )}
      {trailingIcon && <Icon className={title && 'ml-8'} name={trailingIcon} />}
      {numberIndicator && <div className={styles.numberIndicator}>{numberIndicator}</div>}
    </button>
  );
  if (!hoverText) {
    return buttonElement;
  }
  return (
    <MuiTooltip title={hoverText} arrow>
      <span>{buttonElement}</span>
    </MuiTooltip>
  );
}

Button.defaultProps = {};

export default Button;
